if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.slider-dots'),
          imageArrows: this.querySelector('.image-slider-arrows'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        
        // Add thumbnail navigation if thumbnails exist
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        // Add dot navigation event listeners
        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('.slider-dot').forEach((dot) => {
            dot.addEventListener('click', this.onDotClick.bind(this, dot));
          });
        }

        // Add image slider arrow navigation event listeners
        if (this.elements.imageArrows) {
          const prevArrow = this.elements.imageArrows.querySelector('.image-slider-arrow--prev');
          const nextArrow = this.elements.imageArrows.querySelector('.image-slider-arrow--next');
          
          if (prevArrow) {
            prevArrow.addEventListener('click', this.onImageArrowClick.bind(this, 'prev'));
          }
          if (nextArrow) {
            nextArrow.addEventListener('click', this.onImageArrowClick.bind(this, 'next'));
          }
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Initialize active dot and arrows on page load
        this.initializeActiveDot();
        this.updateArrowVisibility();
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.updateActiveDot(event.detail.currentElement);
      }

      onDotClick(dot) {
        const mediaId = dot.getAttribute('data-media-id');
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        
        if (activeMedia) {
          this.setActiveMedia(mediaId, false);
          
          // Scroll to the active media item
          if (this.elements.viewer.slider) {
            this.elements.viewer.slider.scrollTo({ 
              left: activeMedia.offsetLeft,
              behavior: 'smooth'
            });
          }
        }
      }

      onImageArrowClick(direction) {
        const mediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        
        if (!activeMedia || mediaItems.length <= 1) return;
        
        const currentIndex = Array.from(mediaItems).indexOf(activeMedia);
        let nextIndex;
        
        if (direction === 'prev') {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
        } else { // next
          nextIndex = currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
        }
        
        const nextMedia = mediaItems[nextIndex];
        if (nextMedia) {
          const mediaId = nextMedia.getAttribute('data-media-id');
          this.setActiveMedia(mediaId, false);
          
          // Scroll to the next media item
          if (this.elements.viewer.slider) {
            this.elements.viewer.slider.scrollTo({ 
              left: nextMedia.offsetLeft,
              behavior: 'smooth'
            });
          }
        }
      }

      initializeActiveDot() {
        if (!this.elements.dots) return;
        
        // Find the currently active media item
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (activeMedia) {
          this.updateActiveDot(activeMedia);
        }
      }

      updateActiveDot(activeElement) {
        if (!this.elements.dots) return;
        
        const activeMediaId = activeElement.getAttribute('data-media-id');
        
        this.elements.dots.querySelectorAll('.slider-dot').forEach((dot) => {
          const dotMediaId = dot.getAttribute('data-media-id');
          if (dotMediaId === activeMediaId) {
            dot.classList.add('slider-dot--active');
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.classList.remove('slider-dot--active');
            dot.removeAttribute('aria-current');
          }
        });
      }

      updateArrowVisibility() {
        if (!this.elements.imageArrows) return;
        
        const mediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        
        if (!activeMedia || mediaItems.length <= 1) {
          this.elements.imageArrows.style.display = 'none';
          return;
        }
        
        this.elements.imageArrows.style.display = 'block';
        
        const currentIndex = Array.from(mediaItems).indexOf(activeMedia);
        const prevArrow = this.elements.imageArrows.querySelector('.image-slider-arrow--prev');
        const nextArrow = this.elements.imageArrows.querySelector('.image-slider-arrow--next');
        
        // Show/hide arrows based on position (optional - you can remove this for infinite loop)
        if (prevArrow) {
          prevArrow.style.opacity = currentIndex === 0 ? '0.5' : '1';
        }
        if (nextArrow) {
          nextArrow.style.opacity = currentIndex === mediaItems.length - 1 ? '0.5' : '1';
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        
        // Update active dot and arrow visibility
        this.updateActiveDot(activeMedia);
        this.updateArrowVisibility();

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
